import { createContext, useContext, useEffect, useCallback, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { adminSocketService } from '../services/socketService'
import { logger } from '../utils/logger'
import {
  ChatSocketEvent,
  ChatMessage,
  NewMessageEventPayload,
  ChatStatusUpdatedEventPayload,
  NewChatCreatedEventPayload,
  ChatReadEventPayload,
  FloristSocketEvent,
  FloristAssignedPayload,
  FloristConfirmedPayload,
  FloristDeclinedPayload,
  FloristOrderPendingPayload,

  FloristStartedPreparingPayload,
  FlowerInventoryUpdatedPayload,
  FloristAdminNotificationPayload,
} from '@agciceyim/shared'
import { qk } from '../lib/queryKeys'
import type { Socket } from 'socket.io-client'

interface AdminSocketContextType {
  connected: boolean
  socketId: string | undefined
  socket: Socket | null
  joinChatRoom: (chatId: string) => void
  leaveChatRoom: (chatId: string) => void
}

export const AdminSocketContext = createContext<AdminSocketContextType | undefined>(undefined)

export function AdminSocketProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Token izlənməsi ilə Socket.IO-ya qoşulma
  useEffect(() => {
    const token = localStorage.getItem('auth_token')

    if (token && !adminSocketService.connected) {
      logger.socket('Connecting admin socket with token')
      adminSocketService.connect(token)
    } else if (!token && adminSocketService.connected) {
      logger.socket('Disconnecting admin socket (no token)')
      adminSocketService.disconnect()
    }

    // localStorage-da token dəyişikliklərini izləyirik
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'auth_token') {
        const newToken = event.newValue
        if (newToken && !adminSocketService.connected) {
          logger.socket('Token added, connecting socket')
          adminSocketService.connect(newToken)
        } else if (!newToken && adminSocketService.connected) {
          logger.socket('Token removed, disconnecting socket')
          adminSocketService.disconnect()
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      // Sökülərkən soketi ayırmırıq - bu qlobal provayderdir
    }
  }, [])

  // Yeni mesajların idarəedicisi - DAİMİ aktivdir
  const handleNewMessage = useCallback((data: NewMessageEventPayload) => {
    logger.socket('Real-time new message', data)

    // Bütün çat keşlərini yeniləyirik (istənilən filtrlərlə)
    queryClient.setQueriesData({ queryKey: qk.chats() }, (oldData: any) => {
      if (!oldData?.chats) return oldData

      return {
        ...oldData,
        chats: oldData.chats.map((chat: any) => {
          if (chat.id === data.chatId) {
            return {
              ...chat,
              lastMessage: data.message,
              unreadCount: data.message.isFromAdmin ? chat.unreadCount : (chat.unreadCount || 0) + 1,
              updatedAt: data.message.createdAt,
              lastAgentMessageAt: data.message.isFromAdmin ? data.message.createdAt : chat.lastAgentMessageAt,
              lastCustomerMessageAt: !data.message.isFromAdmin ? data.message.createdAt : chat.lastCustomerMessageAt,
              firstResponseAt: data.message.isFromAdmin && !chat.firstResponseAt
                ? data.message.createdAt
                : chat.firstResponseAt,
              supportAgentId: data.message.isFromAdmin
                ? data.message.senderId ?? chat.supportAgentId
                : chat.supportAgentId
            }
          }
          return chat
        })
      }
    })

    // Konkret çatın bütün mesaj keşlərini yeniləyirik
    queryClient.setQueriesData({ queryKey: qk.chatMessages(data.chatId) }, (oldData: any) => {
      // Keş boşdursa, ilk mesajla inisializasiya edirik
      if (!oldData?.messages) {
        return {
          messages: [data.message],
          total: 1,
          page: 1,
          limit: 50
        }
      }

      // Bu mesajın artıq olub-olmadığını yoxlayırıq (dublikatın qarşısını alırıq)
      const messageExists = oldData.messages.some((msg: ChatMessage) => msg.id === data.message.id)
      if (messageExists) return oldData

      return {
        ...oldData,
        messages: [...oldData.messages, data.message],
        total: oldData.total + 1
      }
    })

    // Ardıcıllığı təmin etmək üçün sorğuları etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: qk.chats() })

    // Avtoskroll üçün hadisə göndəririk
    window.dispatchEvent(new CustomEvent('new-chat-message', {
      detail: { message: data.message, chatId: data.chatId }
    }))
  }, [queryClient])

  // Çat statusu dəyişikliyinin idarəedicisi
  const handleChatStatusUpdate = useCallback((data: ChatStatusUpdatedEventPayload) => {
    logger.socket('Real-time chat status update', data)

    // Bütün çat keşlərini yeniləyirik (istənilən filtrlərlə)
    queryClient.setQueriesData({ queryKey: qk.chats() }, (oldData: any) => {
      if (!oldData?.chats) return oldData

      return {
        ...oldData,
        chats: oldData.chats.map((chat: any) => {
          if (chat.id === data.chatId) {
            return {
              ...chat,
              status: data.status
            }
          }
          return chat
        })
      }
    })

    // Konkret çatın keşini yeniləyirik
    queryClient.setQueryData(qk.chat(data.chatId), (oldData: any) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        status: data.status
      }
    })
  }, [queryClient])

  // Sifariş yeniləmələrinin idarəedicisi (təyinatlar, status dəyişikliyi)
  const handleOrderUpdate = useCallback((data: any) => {
    logger.socket('Real-time order update', data)

    // Ardıcıllığı təmin etmək üçün bütün sifariş keşlərini etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    queryClient.invalidateQueries({ queryKey: ['deliveryOrders'] })
    queryClient.invalidateQueries({ queryKey: ['courierOrders'] })
    queryClient.invalidateQueries({ queryKey: ['deliveryStats'] })
    queryClient.invalidateQueries({ queryKey: ['couriers'] })

    // Sifarişlər yeniləndikdə florist keşlərini etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: ['florist', 'pending-orders'] })
    queryClient.invalidateQueries({ queryKey: ['florist', 'confirmations'] })
  }, [queryClient])

  // Florist təyinatı gözləyən yeni sifarişin idarəedicisi
  const handleFloristOrderPending = useCallback((data: FloristOrderPendingPayload) => {
    logger.socket('Real-time florist order pending', data)

    // Florist keşlərini etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: ['florist', 'pending-orders'] })
    queryClient.invalidateQueries({ queryKey: ['orders'] })

    // Adminə yeni sifariş haqqında bildiriş göstər
    toast.info(`Yeni sifariş #${data.order.orderNumber} daxil oldu`, {
      description: 'Florist təyin edilməsi tələb olunur',
      action: {
        label: 'Aç',
        onClick: () => navigate('/florists')
      },
      duration: 10000, // 10 saniyə
    })
  }, [queryClient, navigate])

  // Florist təyinatının idarəedicisi
  const handleFloristAssigned = useCallback((data: FloristAssignedPayload) => {
    logger.socket('Real-time florist assigned', {
      orderId: data.orderId,
      orderNumber: data.orderNumber,
      floristName: data.florist.name
    })

    // Florist keşlərini etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: ['florist', 'pending-orders'] })
    queryClient.invalidateQueries({ queryKey: ['florist', 'confirmations'] })

    // Ümumi sifariş keşlərini yeniləyirik
    handleOrderUpdate(data)
  }, [handleOrderUpdate, queryClient])

  // Sifarişin florist tərəfindən təsdiqlənməsinin idarəedicisi
  const handleFloristConfirmed = useCallback((data: FloristConfirmedPayload) => {
    logger.socket('Real-time florist confirmed', {
      orderId: data.orderId,
      orderNumber: data.orderNumber,
      floristName: data.florist.name,
      ingredientsCount: data.ingredients.length
    })

    // Florist keşlərini etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: ['florist', 'pending-orders'] })
    queryClient.invalidateQueries({ queryKey: ['florist', 'confirmations'] })
    queryClient.invalidateQueries({ queryKey: ['reports', 'flower-usage'] })

    // Ümumi sifariş keşlərini yeniləyirik
    handleOrderUpdate(data)
  }, [handleOrderUpdate, queryClient])

  // Sifarişin florist tərəfindən rədd edilməsinin idarəedicisi
  const handleFloristDeclined = useCallback((data: FloristDeclinedPayload) => {
    logger.socket('Real-time florist declined', {
      orderId: data.orderId,
      orderNumber: data.orderNumber,
      floristName: data.florist.name,
      reason: data.reason
    })

    // Florist keşlərini etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: ['florist', 'pending-orders'] })
    queryClient.invalidateQueries({ queryKey: ['florist', 'confirmations'] })

    // Adminə sifarişin rədd edilməsi haqqında bildiriş göstər
    toast.warning(`Florist ${data.florist.name} #${data.orderNumber} sifarişini rədd etdi`, {
      description: data.reason || 'Yenidən təyin edilməsi tələb olunur',
      action: {
        label: 'Yenidən təyin et',
        onClick: () => navigate('/florists')
      },
      duration: 15000, // 15 saniyə
    })

    // Ümumi sifariş keşlərini yeniləyirik
    handleOrderUpdate(data)
  }, [handleOrderUpdate, queryClient, navigate])

  // Buket hazırlanmasının başlanmasının idarəedicisi
  const handleFloristStartedPreparing = useCallback((data: FloristStartedPreparingPayload) => {
    logger.socket('Real-time florist started preparing', {
      orderId: data.orderId,
      orderNumber: data.orderNumber,
      floristName: data.florist.name
    })

    // Keşləri etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: ['florist', 'confirmations'] })
    queryClient.invalidateQueries({ queryKey: ['orders'] })

    // Ümumi sifariş keşlərini yeniləyirik
    handleOrderUpdate(data)
  }, [handleOrderUpdate, queryClient])

  // Çiçək inventarının yenilənməsinin idarəedicisi
  const handleFloristInventoryUpdated = useCallback((data: FlowerInventoryUpdatedPayload) => {
    logger.socket('Real-time florist inventory updated', {
      variantId: data.variantId,
      flowerName: data.flowerName,
      change: data.change,
      newQuantity: data.newQuantity
    })

    // Çiçək və hesabat keşlərini etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: ['flowers'] })
    queryClient.invalidateQueries({ queryKey: ['reports', 'flower-usage'] })
  }, [queryClient])

  // Admin bildirişlərinin idarəedicisi
  const handleFloristAdminNotification = useCallback((data: FloristAdminNotificationPayload) => {
    logger.socket('Real-time florist admin notification', data.notification)

    // Bildiriş keşlərini etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: ['admin', 'notifications'] })

    // UI-da toast bildirişi göstər
    const notif = data.notification
    // type əvəzinə severity istifadə edirik
    const toastType = notif.severity === 'error' ? 'error' : notif.severity === 'warning' ? 'warning' : 'info'

    toast[toastType](notif.title || 'Bildiriş', {
      description: notif.message,
      duration: 8000,
    })
  }, [queryClient])

  // Kuryer təyinatının idarəedicisi
  const handleCourierAssigned = useCallback((data: any) => {
    logger.socket('Real-time courier assigned', data)
    handleOrderUpdate(data)
  }, [handleOrderUpdate])

  // Buketin hazır olmasının idarəedicisi
  const handleBouquetReady = useCallback((data: any) => {
    logger.socket('Real-time bouquet ready', data)
    handleOrderUpdate(data)
  }, [handleOrderUpdate])

  // Çatdırılmanın başlanmasının idarəedicisi
  const handleDeliveryStarted = useCallback((data: any) => {
    logger.socket('Real-time delivery started', data)
    handleOrderUpdate(data)
  }, [handleOrderUpdate])

  // Çatdırılmanın tamamlanmasının idarəedicisi
  const handleDeliveryCompleted = useCallback((data: any) => {
    logger.socket('Real-time delivery completed', data)
    handleOrderUpdate(data)
  }, [handleOrderUpdate])

  // Yeni çatın idarəedicisi
  const handleNewChat = useCallback((data: NewChatCreatedEventPayload) => {
    logger.socket('Real-time new chat', data)

    // Yeni çatı bütün siyahı keşlərinin əvvəlinə əlavə edirik
    queryClient.setQueriesData({ queryKey: qk.chats() }, (oldData: any) => {
      if (!oldData?.chats) return oldData

      // Bu çatın artıq olub-olmadığını yoxlayırıq
      const chatExists = oldData.chats.some((chat: any) => chat.id === data.chat.id)
      if (chatExists) return oldData

      return {
        ...oldData,
        chats: [
          {
            ...data.chat,
            lastMessage: data.message,
            unreadCount: 1
          },
          ...oldData.chats
        ],
        total: oldData.total + 1
      }
    })

    // Tam məlumatları yükləmək üçün sorğuları etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: qk.chats() })
  }, [queryClient])

  const handleChatRead = useCallback((data: ChatReadEventPayload) => {
    // Ignore customer-read events (customer reading admin messages)
    if (data.readBy === 'customer') return

    // Invalidate all chat list caches to get fresh unreadCount from server
    queryClient.invalidateQueries({ queryKey: qk.chats() })

    // Mark customer messages as read in all matching chatMessages caches
    queryClient.setQueriesData({ queryKey: qk.chatMessages(data.chatId) }, (oldData: any) => {
      if (!oldData?.messages) return oldData

      return {
        ...oldData,
        messages: oldData.messages.map((msg: ChatMessage) =>
          msg.isFromAdmin
            ? msg
            : { ...msg, isNew: false }
        )
      }
    })
  }, [queryClient])

  // 🎫 TICKET SYSTEM EVENT HANDLERS

  const handleTicketAssigned = useCallback((data: any) => {
    logger.socket('Real-time ticket assigned', data)

    // Çat keşlərini etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: qk.chats() })
    queryClient.invalidateQueries({ queryKey: qk.chat(data.ticketId) })

    // Bildiriş göstər
    toast.success(`Tiket ${data.ticketNumber} ${data.agentName} operatoruna təyin edildi`)
  }, [queryClient])

  const handleTicketPriorityChanged = useCallback((data: any) => {
    logger.socket('Real-time ticket priority changed', data)

    // Keşləri etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: qk.chats() })
    queryClient.invalidateQueries({ queryKey: qk.chat(data.ticketId) })

    // Prioritet rəngi ilə bildiriş göstər
    const priorityEmoji = data.newPriority === 'URGENT' ? '🔴' : data.newPriority === 'HIGH' ? '🟠' : '🔵'
    toast.info(`${priorityEmoji} ${data.ticketNumber} tiketinin prioriteti ${data.newPriority} olaraq dəyişdirildi`)
  }, [queryClient])

  const handleTicketClosed = useCallback((data: any) => {
    logger.socket('Real-time ticket closed', data)

    // Keşləri etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: qk.chats() })
    queryClient.invalidateQueries({ queryKey: qk.chat(data.ticketId) })

    // Bildiriş göstər
    toast.success(`${data.ticketNumber} tiketi bağlandı`, {
      description: data.resolution ? `Həll: ${data.resolution.substring(0, 100)}...` : undefined
    })
  }, [queryClient])

  const handleTicketReopened = useCallback((data: any) => {
    logger.socket('Real-time ticket reopened', data)

    // Keşləri etibarsızlaşdırırıq
    queryClient.invalidateQueries({ queryKey: qk.chats() })
    queryClient.invalidateQueries({ queryKey: qk.chat(data.ticketId) })

    // Bildiriş göstər
    toast.info(`${data.ticketNumber} tiketi yenidən açıldı`)
  }, [queryClient])

  // Hadisələrə abunəlik - DAİMİ aktivdir
  useEffect(() => {
    // Chat events
    adminSocketService.on(ChatSocketEvent.NEW_MESSAGE, handleNewMessage)
    adminSocketService.on(ChatSocketEvent.CHAT_STATUS_UPDATED, handleChatStatusUpdate)
    adminSocketService.on(ChatSocketEvent.NEW_CHAT_CREATED, handleNewChat)
    adminSocketService.on(ChatSocketEvent.CHAT_READ, handleChatRead)

    // Ticket events
    adminSocketService.on(ChatSocketEvent.TICKET_ASSIGNED, handleTicketAssigned)
    adminSocketService.on(ChatSocketEvent.TICKET_PRIORITY_CHANGED, handleTicketPriorityChanged)
    adminSocketService.on(ChatSocketEvent.TICKET_CLOSED, handleTicketClosed)
    adminSocketService.on(ChatSocketEvent.TICKET_REOPENED, handleTicketReopened)

    // Order events
    adminSocketService.on('order:updated', handleOrderUpdate)
    adminSocketService.on('order:courier_assigned', handleCourierAssigned)
    adminSocketService.on('order:bouquet_ready', handleBouquetReady)
    adminSocketService.on('order:in_delivery', handleDeliveryStarted)
    adminSocketService.on('order:delivered', handleDeliveryCompleted)

    // Florist events (typed)
    adminSocketService.on('florist:order_pending' as FloristSocketEvent, handleFloristOrderPending)
    adminSocketService.on('florist:assigned' as FloristSocketEvent, handleFloristAssigned)
    adminSocketService.on('florist:confirmed' as FloristSocketEvent, handleFloristConfirmed)
    adminSocketService.on('florist:declined' as FloristSocketEvent, handleFloristDeclined)
    adminSocketService.on('florist:started_preparing' as FloristSocketEvent, handleFloristStartedPreparing)
    adminSocketService.on('florist:bouquet_ready' as FloristSocketEvent, handleBouquetReady)
    adminSocketService.on('florist:inventory_updated' as FloristSocketEvent, handleFloristInventoryUpdated)
    adminSocketService.on('florist:admin_notification' as FloristSocketEvent, handleFloristAdminNotification)

    return () => {
      // Chat events
      adminSocketService.off(ChatSocketEvent.NEW_MESSAGE, handleNewMessage)
      adminSocketService.off(ChatSocketEvent.CHAT_STATUS_UPDATED, handleChatStatusUpdate)
      adminSocketService.off(ChatSocketEvent.NEW_CHAT_CREATED, handleNewChat)
      adminSocketService.off(ChatSocketEvent.CHAT_READ, handleChatRead)

      // Ticket events
      adminSocketService.off(ChatSocketEvent.TICKET_ASSIGNED, handleTicketAssigned)
      adminSocketService.off(ChatSocketEvent.TICKET_PRIORITY_CHANGED, handleTicketPriorityChanged)
      adminSocketService.off(ChatSocketEvent.TICKET_CLOSED, handleTicketClosed)
      adminSocketService.off(ChatSocketEvent.TICKET_REOPENED, handleTicketReopened)

      // Order events
      adminSocketService.off('order:updated', handleOrderUpdate)
      adminSocketService.off('order:courier_assigned', handleCourierAssigned)
      adminSocketService.off('order:bouquet_ready', handleBouquetReady)
      adminSocketService.off('order:in_delivery', handleDeliveryStarted)
      adminSocketService.off('order:delivered', handleDeliveryCompleted)

      // Florist events (typed)
      adminSocketService.off('florist:order_pending' as FloristSocketEvent, handleFloristOrderPending)
      adminSocketService.off('florist:assigned' as FloristSocketEvent, handleFloristAssigned)
      adminSocketService.off('florist:confirmed' as FloristSocketEvent, handleFloristConfirmed)
      adminSocketService.off('florist:declined' as FloristSocketEvent, handleFloristDeclined)
      adminSocketService.off('florist:started_preparing' as FloristSocketEvent, handleFloristStartedPreparing)
      adminSocketService.off('florist:bouquet_ready' as FloristSocketEvent, handleBouquetReady)
      adminSocketService.off('florist:inventory_updated' as FloristSocketEvent, handleFloristInventoryUpdated)
      adminSocketService.off('florist:admin_notification' as FloristSocketEvent, handleFloristAdminNotification)
    }
  }, [
    handleNewMessage,
    handleChatStatusUpdate,
    handleNewChat,
    handleChatRead,
    handleTicketAssigned,
    handleTicketPriorityChanged,
    handleTicketClosed,
    handleTicketReopened,
    handleOrderUpdate,
    handleFloristOrderPending,
    handleFloristAssigned,
    handleFloristConfirmed,
    handleFloristDeclined,
    handleFloristStartedPreparing,
    handleFloristInventoryUpdated,
    handleFloristAdminNotification,
    handleCourierAssigned,
    handleBouquetReady,
    handleDeliveryStarted,
    handleDeliveryCompleted,
  ])

  // Çat otaqlarını idarə etmək üçün funksiyalar
  const joinChatRoom = useCallback((chatId: string) => {
    adminSocketService.joinChatRoom(chatId)
  }, [])

  const leaveChatRoom = useCallback((chatId: string) => {
    adminSocketService.leaveChatRoom(chatId)
  }, [])

  const contextValue: AdminSocketContextType = {
    connected: adminSocketService.connected,
    socketId: adminSocketService.id,
    socket: adminSocketService.socketInstance,
    joinChatRoom,
    leaveChatRoom
  }

  return (
    <AdminSocketContext.Provider value={contextValue}>
      {children}
    </AdminSocketContext.Provider>
  )
}

export function useAdminSocket() {
  const context = useContext(AdminSocketContext)
  if (context === undefined) {
    throw new Error('useAdminSocket must be used within AdminSocketProvider')
  }
  return context
}

